"use client";
import React, { useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { ArtworkMedia } from "@/app/(components)/Basic/ArtworkMedia";
import ViewsIcon from "@/assets/icons/eye-opened.svg";
import LikeIcon from "@/assets/icons/like.svg";
import LikedIcon from "@/assets/icons/liked.svg";
import UploadIcon from "@/assets/icons/upload.svg";
import GiftIcon from "@/assets/icons/gift.svg";
import FollowIcon from "@/assets/icons/add-person.svg";
import FollowingIcon from "@/assets/icons/add-person-fill.svg";
import EditIcon from "@/assets/icons/edit-rounded.svg";
import styles from "./NftHeader.module.css";
import nftsApi from "@/api/nfts/server";
import { ImageOrientation } from "@/api/nfts/types";
import { ArtistTipModal } from "../../Artists/ArtistTipModal";
import { getUsersName } from "@/api/users/helpers";
import { NftShareModal } from "../NftShareModal";
import { SaleLabel } from "@/app/(components)/Basic/SaleLabel";
import { MEDIA_TYPE, getMediaType } from "@/shared/media";
import useFollow from "@/shared/hooks/useFollow";
import { UserLink } from "../../User/UserLink";
import useMe from "@/shared/hooks/useMe";
import useModalToggle from "@/shared/hooks/useToggleModal";
import useWalletSignedFunction from "@/shared/hooks/useWalletSignedFunction";
import { ImageTypeHandler } from "@/app/(components)/Basic/ImageTypeHandler";
import ordersApi from "@/api/orders/client";
import viewsApi from "@/api/views/client";
import { useQuery, useMutation } from "@tanstack/react-query";
import { NftStats } from "../NftStats";
import useView from "@/shared/hooks/useView";
import { FullScreen } from "@/app/(components)/Basic/FullScreen";
import { useRouter } from "next/navigation";
import { calculateDisplayedDimensions } from "./helpers";
import useNftLike from "../../Likes/hooks/useNftLike";
import { Tooltip } from "@/app/(components)/Basic/Tooltip";
import { DEFAULT_NFT_SIZE } from "@/shared/constants";
import NftPlaceholder from "@/assets/images/nftPlaceholder.jpg";
import { OrderType } from "@/api/orders/types";
import { isUserPrivateAuctionParticipant } from "../../Trade/TradeActions/helpers";

export interface Props {
  nftId: string;
  isUserFollowing: boolean;
  artistName?: string;
}

function NftHeader({ nftId, artistName, isUserFollowing }: Props) {
  const { me: user } = useMe();
  const router = useRouter();

  const { data: nft } = useQuery({
    queryKey: ["nft", nftId],
    queryFn: async () => {
      const nftItem = await nftsApi.getNftDetails(nftId);

      return { ...nftItem, ...calculateDisplayedDimensions(nftItem) };
    },
  });

  const [isSmall, setIsSmall] = useState(false);
  const [isTipModalOpen, toggleTipModal] = useModalToggle();
  const [isShareModalOpen, toggleShareModal] = useModalToggle();

  const { mutateAsync: setViewForNft } = useMutation({
    mutationFn: (nftId: string) => viewsApi.setViewForNft(nftId),
  });

  useEffect(() => {
    if (nft?.id) {
      setViewForNft(nft.id);
    }
  }, [nft?.id, setViewForNft]);

  const { isFollowing, toggleFollow } = useFollow({
    userId: nft?.artist.id,
    isMeFollowing: isUserFollowing,
  });

  const { isLikedByMe, toggleLike, likeCount } = useNftLike({
    nftId: nft?.id,
    user,
  });

  const { viewsCount } = useView(nft?.id);

  const checkedToggle = useWalletSignedFunction(toggleTipModal);

  const imageWidth =
    nft?.mediaType === MEDIA_TYPE.VIDEO ? 800 : nft?.imageWidth;
  const imageHeight =
    nft?.mediaType === MEDIA_TYPE.VIDEO ? 600 : nft?.imageHeight;

  const { data: orders } = useQuery({
    queryKey: ["orders-for-nft", nft?.id],
    queryFn: () =>
      nft?.id ? ordersApi.getOrdersForNft(nft?.id) : Promise.resolve({}),
  });

  const auction = orders?.auction || orders?.privateAuction || null;

  const isPrivateAuctionHidden = useMemo(
    () =>
      !!auction &&
      auction?.type === OrderType.PRIVATE_AUCTION &&
      !isUserPrivateAuctionParticipant(user!, auction),
    [auction, user],
  );

  useEffect(() => {
    const artistSection = document.querySelector("#artist-section");
    const nftBlurImageSection = document.querySelector(
      "#nft-blur-image-section",
    );

    let isArtistInView = false;
    let isNftBlurImageOutOfView = false;

    const THRESHOLD = nft!.imageHeight < 550 ? 0.6 : 0.3;

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.target === nftBlurImageSection) {
          isNftBlurImageOutOfView = !entry.isIntersecting;
        } else if (entry.target === artistSection) {
          isArtistInView = entry.isIntersecting;
        }

        if (isNftBlurImageOutOfView && !isArtistInView) {
          setIsSmall(true);
        } else {
          setIsSmall(false);
        }
      });
    };

    const observerOptions: IntersectionObserverInit = {
      root: null,
      threshold: THRESHOLD,
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    if (artistSection) {
      observer.observe(artistSection);
    }

    if (nftBlurImageSection) {
      observer.observe(nftBlurImageSection);
    }

    return () => {
      if (artistSection) observer.unobserve(artistSection);
      if (nftBlurImageSection) observer.unobserve(nftBlurImageSection);
    };
  }, []);

  const iconsConfig = [
    {
      show: true,
      action: toggleShareModal,
      icon: (
        <Tooltip text="Share" position={["center", "top"]} scaleDistance={2.4}>
          <UploadIcon className="w-10 h-10" />
        </Tooltip>
      ),
    },
    {
      show: user?.id !== nft?.artist.id,
      action: checkedToggle,
      icon: (
        <Tooltip
          text="Tip an artist"
          position={["center", "top"]}
          scaleDistance={1.9}
          tooltipClassName="w-16"
        >
          <GiftIcon className="w-10 h-10" />
        </Tooltip>
      ),
    },
    {
      show: user?.id !== nft?.artist.id,
      action: toggleFollow,
      icon: isFollowing ? (
        <Tooltip
          text="Unfollow"
          position={["center", "top"]}
          scaleDistance={2.4}
        >
          <FollowingIcon className="w-10 h-10" />
        </Tooltip>
      ) : (
        <Tooltip text="Follow" position={["center", "top"]} scaleDistance={2.4}>
          <FollowIcon className="w-10 h-10" />
        </Tooltip>
      ),
    },
    {
      show: user?.id === nft?.artist.id,
      action: () => router.push(`/edit/nft/${nft?.id}`),
      icon: (
        <Tooltip text="Edit" position={["center", "top"]} scaleDistance={2.4}>
          <EditIcon className="w-10 h-10" />
        </Tooltip>
      ),
    },
  ];

  const renderIcons = () =>
    iconsConfig.map((iconData, index) =>
      iconData.show !== false ? (
        <button key={index} onClick={iconData.action}>
          {iconData.icon}
        </button>
      ) : null,
    );

  const nftStatsConfig = [
    {
      id: 1,
      icon: isLikedByMe ? (
        <Tooltip text="Like" position={["center", "top"]} scaleDistance={2}>
          <LikedIcon className="w-6 fill-white" />
        </Tooltip>
      ) : (
        <Tooltip text="Like" position={["center", "top"]} scaleDistance={2}>
          <LikeIcon className="w-6 fill-white" />
        </Tooltip>
      ),
      count: likeCount,
      action: toggleLike,
    },
    {
      id: 2,
      icon: (
        <Tooltip text="Views" position={["center", "top"]} scaleDistance={2.05}>
          <ViewsIcon className="w-6 fill-white" />
        </Tooltip>
      ),
      count: viewsCount,
    },
  ];

  if (!nft) {
    return null;
  }

  return (
    <>
      <ArtistTipModal
        isOpen={isTipModalOpen}
        onClose={toggleTipModal}
        imageUrl={nft.artist.imageUrl!}
        imagePlaceholder={nft.artist.imagePlaceholder}
        fullName={getUsersName(nft.artist, true)}
        recipient={nft.artist}
      />
      <NftShareModal
        isOpen={isShareModalOpen}
        onClose={toggleShareModal}
        nftId={nft.id}
      />

      {isSmall ? (
        <div
          className={twMerge(
            "hidden lg:block lg:z-30 lg:sticky lg:top-[132px]",
            styles.small,
          )}
        >
          <div
            className={twMerge(
              "h-[200px] overflow-hidden absolute right-0 left-0 z-0 bg-secondary lg:transition-all",
            )}
          >
            <ImageTypeHandler
              src={nft.thumbnail || nft.image || NftPlaceholder.src}
              placeholder={nft.placeholder}
              type={nft.mediaType}
              fill={true}
              alt={nft.name || nft.id}
              className="w-full blur-2xl opacity-90"
            />
            <div className="absolute top-0 bottom-0 right-0 left-0" />
          </div>

          <div
            className={twMerge(
              `mx-auto overflow-hidden text-white flex max-w-[1024px] text-center flex-col lg:transition-all lg:flex-row-reverse lg:relative lg:z-1 lg:justify-end lg:text-left lg:p-6`,
            )}
          >
            <div className="relative pr-52 overflow-hidden flex flex-col">
              <div
                className={twMerge(
                  "lg:text-[50px] leading-tight font-semibold lg:transition-all truncate",
                )}
              >
                {nft.name || nft.id}
              </div>
              <div className={twMerge("text-body-3 lg:transition-all")}>
                <UserLink
                  artist={nft.artist}
                  className="w-fit"
                  tooltipPosition={["right", "middle"]}
                >
                  by {artistName}
                </UserLink>
              </div>
              <div className="mt-auto flex flex-col gap-y-1">
                {!isPrivateAuctionHidden ? (
                  <SaleLabel
                    auction={auction}
                    className="h-8 w-fit flex items-center gap-x-2"
                  />
                ) : null}
                <NftStats stats={nftStatsConfig} />
              </div>
            </div>
            <div
              className={twMerge(
                "flex mt-6 px-4 flex-col-reverse lg:transition-all items-center",
                "lg:flex-row lg:pl-0 lg:pr-10 lg:mt-0 shrink-0 grow-0",
              )}
            >
              <ImageTypeHandler
                src={nft.thumbnail || nft.image || NftPlaceholder.src}
                placeholder={nft.placeholder}
                type={nft.mediaType}
                width={imageWidth || DEFAULT_NFT_SIZE}
                height={imageHeight || DEFAULT_NFT_SIZE}
                alt={nft.name || nft.id}
                className="relative max-h-[150px] w-auto"
              />
              <div
                className={twMerge(
                  "flex flex-row right-0 gap-4 static mb-4 lg:transition-all lg:absolute lg:flex-row lg:right-0 lg:top-[50%] lg:-translate-y-[50%]",
                )}
              >
                {renderIcons()}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <div
        className={twMerge(
          "relative mb-20 z-20 lg:transition-all lg:duration-500",
          isSmall && "lg:opacity-0",
        )}
      >
        <div
          id="nft-blur-image-section"
          className={twMerge(
            "max-h-[620px] min-h-[230px] lg:min-h-[500px] overflow-hidden absolute right-0 left-0 z-0 bg-overlay lg:transition-all",
            orders?.auction && "min-h-[360px]",
            nft.imageOrientation == ImageOrientation.Landscape
              ? "h-[85%]"
              : "h-[70%]",
          )}
        >
          <ImageTypeHandler
            src={nft.thumbnail || nft.image || NftPlaceholder.src}
            placeholder={nft.placeholder}
            type={nft.mediaType}
            width={nft.imageWidth}
            height={nft.imageHeight}
            alt={nft.name || nft.id}
            className="object-cover w-full blur-2xl opacity-90"
          />
        </div>
        <div
          className={twMerge(
            `mx-auto text-white flex max-w-[1024px] text-center flex-col lg:transition-all`,
            "lg:pt-15 lg:items-center",
          )}
        >
          <div className="relative max-w-full">
            <div
              className={twMerge(
                "text-[40px] lg:transition-all break-words px-6 lg:px-0 leading-tight lg:text-h1 pt-4 lg:pt-0 max-w-full",
              )}
            >
              {nft.name || nft.id}
            </div>
            <div
              className={twMerge(
                "text-body-3 lg:text-body-2 lg:transition-all",
              )}
            >
              <UserLink
                artist={nft.artist}
                tooltipPosition={["center", "bottom"]}
              >
                by {artistName}
              </UserLink>
            </div>
            {!isPrivateAuctionHidden ? (
              <div className="h-10">
                <SaleLabel auction={auction} className="mt-4" />
              </div>
            ) : null}
            <div className="flex justify-center mt-8">
              <NftStats stats={nftStatsConfig} />
            </div>
          </div>
          <div
            className={twMerge(
              "flex mt-6 px-4 flex-col-reverse lg:transition-all items-center",
              "lg:px-20 relative ",
            )}
          >
            <FullScreen
              nftId={nft.id}
              type={nft.mediaType!}
              audioSrc={nft.audioGuide}
              mediaSrc={nft.image || NftPlaceholder.src}
            />

            <ArtworkMedia
              fullScreenEnabled
              media={nft.animationUrl || nft.image || NftPlaceholder.src}
              placeholder={nft.placeholder}
              type={
                nft.animationUrl
                  ? getMediaType(nft.animationUrl)
                  : nft.mediaType || MEDIA_TYPE.IMAGE
              }
              width={nft.displayedWidth || DEFAULT_NFT_SIZE}
              height={nft.displayedHeight || DEFAULT_NFT_SIZE}
              className={twMerge(
                `transition-all ${
                  nft.mediaType === MEDIA_TYPE.MODEL
                    ? "lg:min-h-[500px] lg:min-w-screen"
                    : ""
                }`,
                `${nft.mediaType === MEDIA_TYPE.VIDEO ? "lg:w-[800px]" : ""}`,
              )}
            />
            <div
              className={twMerge(
                "z-20 flex flex-row right-0 gap-4 mb-4 lg:transition-all lg:absolute",
                "lg:flex-col lg:right-0 lg:top-0 lg:pl-10 lg:-mb-0",
              )}
            >
              {renderIcons()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NftHeader;
